import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { queryClient, queryKeys } from './query-client';
import { WebSearchResult } from '../pages/api/search';

export const SEARCH_DEBOUNCE_MS = 250;

export type SearchState =
  | { kind: 'idle' }
  | { kind: 'loading'; query: string }
  | { kind: 'error'; query: string; message: string }
  | { kind: 'ready'; query: string; results: WebSearchResult[] };

async function fetchSearch(query: string, signal?: AbortSignal): Promise<WebSearchResult[]> {
  const res = await apiFetch(`${API_BASE}/api/search?q=${encodeURIComponent(query)}`, {
    credentials: 'include',
    signal,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Search failed (${res.status}) ${text.slice(0, 80)}`);
  }
  const data = (await res.json()) as { results?: WebSearchResult[] };
  return data.results || [];
}

function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

export function useConversationSearch(query: string): SearchState {
  const trimmed = query.trim();
  const debounced = useDebounced(trimmed, SEARCH_DEBOUNCE_MS);

  const { data, error } = useQuery({
    queryKey: queryKeys.search(debounced),
    queryFn: ({ signal }) => fetchSearch(debounced, signal),
    enabled: debounced.length > 0,
    // Re-typing a previous query returns its cached results instantly.
    staleTime: Infinity,
  });

  if (!trimmed) return { kind: 'idle' };
  // A previously-fetched query resolves instantly, skipping the debounce.
  const cached = queryClient.getQueryData<WebSearchResult[]>(queryKeys.search(trimmed));
  if (cached) return { kind: 'ready', query: trimmed, results: cached };
  if (trimmed !== debounced) return { kind: 'loading', query: trimmed };
  if (error instanceof Error) return { kind: 'error', query: debounced, message: error.message };
  if (data) return { kind: 'ready', query: debounced, results: data };
  return { kind: 'loading', query: trimmed };
}

export type { WebSearchResult };
